import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  CircularProgress,
  FormHelperText,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomDropzone from "../../CustomDropzone";
import RegionCommuneSelector from "../../RegionCommuneSelector";

const DEFAULT_FORM_STATE = {
  name: "",
  courier: "",
  admin: "",
  price: 0,
  country: "",
  communes: [],
};

/**
 * @typedef {object} SelectOption
 * @property {string|number} value
 * @property {string} label
 */

/**
 * @typedef {object} ShippingZoneData
 * @property {string} name
 * @property {string} courier
 * @property {number} admin
 * @property {number} price
 * @property {string} country
 * @property {Array<number>} communes
 * @property {File} [file]
 * @property {boolean} [isMassiveUpload]
 */

/**
 * A modal for creating or editing a ShippingZone
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function(ShippingZoneData): Promise<void>} props.onConfirm
 * @param {boolean} props.loading
 * @param {Array<SelectOption>} [props.courierOptions=[]]
 * @param {Array<SelectOption>} [props.adminOptions=[]]
 * @param {Array<object>} [props.regions=[]]
 */
const CreateShippingZoneModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  courierOptions = [],
  adminOptions = [],
  regions,
  loadingRegions,
  fetchRegions,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [excelFile, setExcelFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const isFormValid =
    formData.name.trim() !== "" &&
    ![null, ""].includes(formData.admin) &&
    formData.courier !== "" &&
    formData.price >= 0;

  useEffect(() => {
    if (open) {
      setFormData(DEFAULT_FORM_STATE);
      setExcelFile(null);
      setFormErrors({});
    }
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));

    if (formData.country === "MX") {
      setFormData((previous) => ({ ...previous, communes: [] }));
    }

    if (formErrors[name]) {
      setFormErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handleRegionCommuneChange = (newSelectedIds) => {
    setFormData((prev) => ({
      ...prev,
      communes: newSelectedIds,
    }));

    if (formErrors.communes) {
      setFormErrors((prev) => ({ ...prev, communes: null }));
    }
  };

  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setExcelFile(acceptedFiles[0]);
      if (formErrors.file) {
        setFormErrors((prev) => ({ ...prev, file: null }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = "El nombre es requerido";
    if (!formData.admin) errors.admin = "Seleccionar Admin es requerido";
    if (!formData.courier) errors.courier = "El courier es requerido";
    if (!formData.country) errors.country = "Seleccionar el país es requerido";
    if (formData.price === "" || formData.price < 0)
      errors.price = "Debes ingresar un precio válido";

    if (formData.country === "CL" && formData.communes.length === 0) {
      errors.communes = "Al menos debes seleccionar una comuna para Chile";
    }

    if (formData.country === "MX" && !excelFile) {
      errors.file = "Debes adjuntar el archivo Excel con los Códigos Postales";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = { ...formData };
    payload.commune_ids = payload.communes;
    delete payload.communes;

    onConfirm({
      ...payload,
      isMassiveUpload: formData.country === "MX",
      file: excelFile,
    });
  };

  useEffect(() => {
    if (formData.country === "CL" && regions.length === 0) {
      fetchRegions("CL");
    }
  }, [formData.country, regions.length, fetchRegions]);

  const getCommuneNameById = (id) => {
    for (const region of regions) {
      const found = region.communes.find((c) => c.id === id);
      if (found) return found.name;
    }
    return id;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Crear Nueva Zona</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          name="name"
          label="Nombre de la Zona"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleChange}
          error={!!formErrors.name}
          helperText={formErrors.name}
          required
        />
        <TextField
          margin="dense"
          id="price"
          name="price"
          label="Precio"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.price}
          onChange={handleChange}
          error={!!formErrors.price}
          helperText={formErrors.price}
        />

        <FormControl
          fullWidth
          margin="dense"
          error={!!formErrors.admin}
          required
        >
          <InputLabel id="admin-select-label">Admin</InputLabel>
          <Select
            labelId="admin-select-label"
            id="admin"
            name="admin"
            value={formData.admin}
            label="Admin"
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {adminOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {formErrors.admin && (
            <FormHelperText>{formErrors.admin}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          margin="dense"
          error={!!formErrors.courier}
          required
        >
          <InputLabel id="courier-select-label">Courier</InputLabel>
          <Select
            labelId="courier-select-label"
            id="courier"
            name="courier"
            value={formData.courier}
            label="Courier"
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {courierOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {formErrors.courier && (
            <FormHelperText>{formErrors.courier}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          margin="dense"
          error={!!formErrors.country}
          required
        >
          <InputLabel id="country-select-label">
            País / Método de carga
          </InputLabel>
          <Select
            labelId="country-select-label"
            id="country"
            name="country"
            value={formData.country}
            label="País / Método de carga"
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            <MenuItem value="CL">Chile (Selección Manual)</MenuItem>
            <MenuItem value="MX">México (Carga Masiva Excel)</MenuItem>
          </Select>
          {formErrors.country && (
            <FormHelperText>{formErrors.country}</FormHelperText>
          )}
        </FormControl>

        {formData.country === "CL" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selección de Comunas
            </Typography>

            {loadingRegions ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Cargando estructura geográfica...
                </Typography>
              </Box>
            ) : (
              <RegionCommuneSelector
                regions={regions}
                selectedCommunesIds={formData.communes}
                onChange={handleRegionCommuneChange}
              />
            )}

            {formErrors.communes && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {formErrors.communes}
              </Typography>
            )}

            {formData.communes.length > 0 && !loadingRegions && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 2,
                  maxWidth: "100%",
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {formData.communes.map((communeId) => (
                    <Chip
                      key={communeId}
                      label={getCommuneNameById(communeId)}
                      onDelete={() => {
                        handleRegionCommuneChange(
                          formData.communes.filter((id) => id !== communeId),
                        );
                      }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {formData.country === "MX" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Carga Masiva de Códigos Postales
            </Typography>

            {!excelFile ? (
              <CustomDropzone
                width="100%"
                onDrop={handleFileDrop}
                accept={{
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [".xlsx"],
                  "application/vnd.ms-excel": [".xls"],
                }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#e0f2f1",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <i className="material-icons" style={{ color: "#00e1be" }}>
                    description
                  </i>
                  <Typography variant="body2" fontWeight="bold">
                    {excelFile.name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setExcelFile(null)}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}

            {formErrors.file && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {formErrors.file}
              </Typography>
            )}
          </Box>
        )}

        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateShippingZoneModal;
